import { HttpClient, AuthError, isAbortError } from './HttpClient.js';
import { SessionStorage } from './SessionStorage.js';

/**
 * ApiClient - Handles authenticated API requests
 * Executes requests instantly using environment configuration
 */
export class ApiClient {
  constructor(authService = null) {
    this.httpClient = new HttpClient();
    this.sessionStorage = new SessionStorage();
    this.authService = authService; // Will be set after AuthService is created
    
    // Environment configuration
    this.baseUrl = process.env.REACT_APP_CLINIC_SERVER_URL;
    this.authBaseUrl = process.env.REACT_APP_IDENTITY_SERVER_URL;
    
    console.log('ApiClient initialized with baseUrl:', this.baseUrl);
  }

  /**
   * Set the AuthService reference (to avoid circular dependency) :)
   */
  setAuthService(authService) {
    this.authService = authService;
  }

  /**
   * Make an API request
   * @param {string} path - API path (e.g., "/api/documents/fetch")
   * @param {Object} options - Fetch options; pass `signal` from an AbortController to cancel
   * @param {Function} responseParser - Function to parse response (default: parse JSON)
   * @returns {Promise<any>} Parsed response data
   */
  async request(path, options = {}, responseParser = null) {
    // Default response parser
    const parser = responseParser || ((response) => this.httpClient.parseJson(response));
    
    const baseUrl = this.baseUrl;
    if (!baseUrl) {
      throw new Error('Base URL not available');
    }
    
    const fullUrl = `${baseUrl}${path}`;
    console.log(`[ApiClient] Executing request: ${fullUrl}`);
    
    try {
      const response = await this.httpClient.request(fullUrl, options);
      this.httpClient.checkResponse(response);
      return await parser(response);
    } catch (error) {
      // Handle auth errors, but never treat a cancelled request as a session problem
      if (error instanceof AuthError && !isAbortError(error)) {
        await this.redirectToLogin();
      }
      throw error;
    }
  }

  /**
   * GET request
   */
  async get(path, options = {}) {
    return this.request(path, { ...options, method: 'GET' });
  }

  /**
   * POST request
   */
  async post(path, body = null, options = {}) {
    return this.request(path, {
      ...options,
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined
    });
  }

  /**
   * GET request with blob response
   */
  async getBlob(path, options = {}) {
    return this.request(
      path,
      { ...options, method: 'GET' },
      (response) => this.httpClient.parseBlob(response)
    );
  }

  /**
   * Redirect to login page
   */
  async redirectToLogin() {
    // Prevent redirect loops
    if (this.sessionStorage.isAuthRedirectInProgress()) {
      console.log('Auth redirect already in progress, skipping');
      return;
    }
    
    console.log('Redirecting to login - clearing session first');
    
    // Set redirect flag
    this.sessionStorage.setAuthRedirect();
    this.sessionStorage.clearUserData();
    
    // Clear server-side session
    try {
      await this.httpClient.post(`${this.authBaseUrl}/auth/logout`);
      console.log('Server session cleared');
    } catch (error) {
      console.error('Error clearing server session:', error);
    }
    
    // Redirect to login
    window.location.href = '/login';
  }

  /**
   * Cleanup
   */
  cleanup() {
    // No cleanup needed
  }
}

